import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Upload, message, Radio, Switch, Spin } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import CategoryServ from "../service/CategoryServ";
import GlobalButton from "../util/GlobalButton";
import SafeImage, { getImageUrl } from "./SafeImage";

/* ── Premium Status Toggle matching the ACTIVE/INACTIVE table style ── */
const StatusToggle = ({ checked, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '6px 14px 6px 10px',
      borderRadius: 20,
      background: checked ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.08)',
      border: `1.5px solid ${checked ? '#86efac' : '#fca5a5'}`,
      transition: 'all 0.25s ease',
      cursor: 'pointer',
    }} onClick={() => onChange && onChange(!checked)}>
      <Switch
        checked={checked}
        onChange={onChange}
        size="default"
        style={{
          backgroundColor: checked ? '#22c55e' : '#ef4444',
          minWidth: 40,
        }}
      />
      <span style={{
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '0.5px',
        color: checked ? '#16a34a' : '#dc2626',
        textTransform: 'uppercase',
      }}>
        {checked ? 'Active' : 'Inactive'}
      </span>
    </div>
  </div>
);




const AddCategory = ({ open, refreshCategory, categoryDetails, isEditing, onClose }) => {
  const categoryId = categoryDetails?._id || categoryDetails?.id;
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [inputType, setInputType] = useState("upload");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState("");

  // Load form data on edit
  useEffect(() => {
    if (isEditing && categoryDetails) {
      form.setFieldsValue({
        name: categoryDetails.name,
        imageUrl: categoryDetails.imageUrl,
        description: categoryDetails.description,
        isLive: categoryDetails.isLive !== undefined ? categoryDetails.isLive : true
      });
      setImagePreview(categoryDetails.imageUrl || "");
      if (categoryDetails.imageUrl && (categoryDetails.imageUrl.startsWith('http') || categoryDetails.imageUrl.startsWith('https'))) {
        setInputType("url");
      } else {
        setInputType("upload");
      }
    } else {
      form.resetFields();
      setImagePreview("");
      setInputType("upload");
    }
  }, [isEditing, categoryDetails, form]);

  // Image upload handler
  const handleImageUpload = async (file) => {
    try {
      setIsUploadingImage(true);
      setImageError("");
      const formData = new FormData();
      formData.append("image", file);
      const res = await CategoryServ.uploadImage(formData);
      if (res && res.imageUrl) {
        const fullUrl = getImageUrl(res.imageUrl);
        setImagePreview(fullUrl);
        form.setFieldsValue({ imageUrl: res.imageUrl });
        message.success("Image uploaded successfully");
      }
    } catch (error) {
      const errorMsg = error.message || error.response?.data?.message || "Failed to upload image";
      setImageError(errorMsg);
      message.error(errorMsg);
    } finally {
      setIsUploadingImage(false);
    }
    return false;
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      const payload = {
        name: values.name,
        imageUrl: values.imageUrl,
        description: values.description,
        parentId: null,
        isLive: values.isLive
      };
      if (isEditing && categoryId) {
        await CategoryServ.update(categoryId, payload);
      } else {
        await CategoryServ.create(payload);
      }
      handleClose(true);
    } catch (error) {
      console.error("Error saving category:", error);
      message.error(error.message || "Failed to save category");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (shouldRefresh) => {
    refreshCategory(shouldRefresh);
    form.resetFields();
    setImagePreview("");
    if (onClose) onClose();
  };

  /* ── Shared label style matching the Shop modal ─────────── */
  const labelStyle = {
    fontSize: 11,
    fontWeight: 800,
    color: '#0B1F4D',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: 6
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12,
            background: 'linear-gradient(135deg, #4776E6 0%, #0B1F4D 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
            </svg>
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#1a1d2e' }}>
              {isEditing ? "Edit Category" : "Create Category"}
            </h2>
            <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', fontWeight: 500, marginTop: 1 }}>
              {isEditing ? "Update category details" : "Standardized Category Management"}
            </p>
          </div>
        </div>
      }
      open={open}
      onCancel={() => handleClose(false)}
      width={700}
      className="custom-modal"
      centered
      styles={{
        header: { borderBottom: '3px solid transparent', borderImage: 'linear-gradient(90deg, #FF8800, #FF5E00, #4776E6, #0B1F4D) 1', paddingBottom: 14 },
      }}
      footer={
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 0 4px 0', borderTop: '1.5px solid #f1f5f9',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Category Management</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => handleClose(false)}
              disabled={submitting}
              style={{
                height: 38, padding: '0 20px', borderRadius: 8,
                border: '1.5px solid #e2e8f0', background: '#fff',
                fontSize: 13, fontWeight: 700, color: '#64748b', cursor: 'pointer',
              }}
            >
              CANCEL
            </button>
            <button
              onClick={() => form.submit()}
              disabled={submitting}
              style={{
                height: 38, padding: '0 28px', borderRadius: 8,
                border: 'none',
                background: 'linear-gradient(135deg, #4776E6 0%, #0B1F4D 100%)',
                fontSize: 13, fontWeight: 800, color: '#fff',
                cursor: submitting ? 'not-allowed' : 'pointer', letterSpacing: '0.3px',
                display: 'flex', alignItems: 'center', gap: 6,
                boxShadow: '0 4px 14px rgba(71,118,230,0.3)',
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? (isEditing ? "UPDATING..." : "CREATING...") : (isEditing ? "UPDATE" : "CREATE")}
              {!submitting && (
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                </svg>
              )}
            </button>
          </div>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        size="middle"
        requiredMark={false}
        onFinish={handleSubmit}
        onFinishFailed={() => message.error("Please fill required fields")}
        onValuesChange={(changedValues) => {
          if (changedValues.imageUrl !== undefined) {
            setImagePreview(changedValues.imageUrl);
          }
        }}
      >
        {/* ── 2-Column Grid: Left = Name + Description | Right = Image ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', alignItems: 'start' }}>

          {/* ── LEFT COLUMN ─────────────────────────────────────── */}
          <div>
            {/* Category Name */}
            <div style={{ marginBottom: 16 }}>
              <span style={labelStyle}>Category Name <span style={{ color: '#ef4444' }}>*</span></span>
              <Form.Item name="name" noStyle rules={[{ required: !isEditing, message: "Category name is required" }]}>
                <Input
                  placeholder="Enter category name"
                  style={{
                    height: 40,
                    borderRadius: 10,
                    border: '1.5px solid #e2e8f0',
                    fontSize: 13,
                  }}
                />
              </Form.Item>
            </div>

            {/* Category Description */}
            <div style={{ marginBottom: 16 }}>
              <span style={labelStyle}>Category Description <span style={{ color: '#ef4444' }}>*</span></span>
              <Form.Item name="description" noStyle rules={[{ required: !isEditing, message: "Description is required" }]}>
                <Input.TextArea
                  rows={6}
                  placeholder="Enter category description"
                  style={{
                    borderRadius: 10,
                    border: '1.5px solid #e2e8f0',
                    fontSize: 13,
                    resize: 'none',
                  }}
                />
              </Form.Item>
            </div>

            {/* Status */}
            <div style={{ marginTop: 4 }}>
              <span style={{ ...labelStyle, marginBottom: 8 }}>STATUS</span>
              <Form.Item name="isLive" valuePropName="checked" initialValue={true} noStyle>
                <StatusToggle />
              </Form.Item>
            </div>
          </div>

          {/* ── RIGHT COLUMN ─────────────────────────────────────── */}
          <div>
            {/* Image Source Toggle */}
            <div style={{ marginBottom: 10 }}>
              <span style={labelStyle}>Category Image Source</span>
              <Radio.Group
                value={inputType}
                onChange={(e) => {
                  setInputType(e.target.value);
                  const currentVal = form.getFieldValue("imageUrl");
                  if (e.target.value === 'url' && currentVal && String(currentVal).startsWith('data:')) {
                    form.setFieldsValue({ imageUrl: '' });
                    setImagePreview('');
                  }
                }}
              >
                <Radio value="upload">Upload Image</Radio>
                <Radio value="url">Use Image URL</Radio>
              </Radio.Group>
            </div>

            {/* Upload input */}
            {inputType === 'upload' ? (
              <div style={{ marginBottom: 24 }}>
                <Form.Item name="imageUrl" rules={[{ required: !isEditing, message: "Category image is required" }]} noStyle>
                  <Input type="hidden" />
                </Form.Item>
                <Upload
                  className="upload-full-width"
                  beforeUpload={handleImageUpload}
                  showUploadList={false}
                  accept="image/*"
                  style={{ width: '100%' }}
                >
                  <div style={{ width: '100%' }}>
                    {isUploadingImage ? (
                      <div style={{ padding: 60, textAlign: 'center', background: '#f8fafc', borderRadius: 12, border: '2px dashed #cbd5e1' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 10, color: '#64748b', fontSize: 13, fontWeight: 500 }}>Uploading Image...</div>
                      </div>
                    ) : imagePreview ? (
                      <div className="banner-image-preview" style={{
                        width: '100%', height: 260, borderRadius: 12, overflow: 'hidden',
                        border: '2px dashed #c7d2fe', cursor: 'pointer', position: 'relative'
                      }}>
                        <SafeImage src={imagePreview} alt="Category Preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        <div className="banner-overlay">
                          <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>Click to Change</span>
                        </div>
                      </div>
                    ) : (
                      <div className="banner-upload-placeholder" style={{
                        padding: '60px 20px', textAlign: 'center', background: '#f8fafc', borderRadius: 12,
                        border: '2px dashed #cbd5e1', cursor: 'pointer', transition: 'all 0.3s ease'
                      }}>
                        <div style={{
                          width: 48, height: 48, background: '#e0e7ff', borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px'
                        }}>
                          <UploadOutlined style={{ fontSize: 24, color: '#4f46e5' }} />
                        </div>
                        <div style={{ color: '#334155', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                          Upload Category Image
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: 12 }}>
                          PNG, JPG, WebP up to 3MB
                        </div>
                      </div>
                    )}
                  </div>
                </Upload>
                <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', textAlign: 'center' }}>
                  Maximum upload size: <strong>3MB</strong> (JPG, PNG, WEBP)
                </div>
                {imageError && (
                  <div style={{ marginTop: 8, fontSize: 13, color: '#ef4444', textAlign: 'center', background: '#fef2f2', padding: '8px', borderRadius: '6px', border: '1px solid #fca5a5' }}>
                    {imageError}
                  </div>
                )}
              </div>
            ) : (
              <>
                <Form.Item
                  name="imageUrl"
                  rules={[{ required: !isEditing, message: "Image URL is required" }]}
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="https://example.com/image.jpg"
                    style={{
                      height: 40,
                      borderRadius: 10,
                      border: '1.5px solid #e2e8f0',
                      fontSize: 13,
                    }}
                  />
                </Form.Item>
                {imagePreview && (
                  <div style={{ borderRadius: 12, overflow: 'hidden', border: '1.5px solid #e2e8f0', height: 200 }}>
                    <SafeImage
                      src={imagePreview}
                      alt="Category Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default AddCategory;
